import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableDefinition } from '~/Components/DataTable/DataTable.types';
import {
  IFitoList,
  IFitoListSettings,
} from '~/Components/FitoList/FitoListUncontrolled/interfaces';
import { Route } from '~/Constants/Routes.constants';
import { routeReplacer } from '~/Helpers/String.helper';
import { useNavigation, useProvider } from '~/Hooks';
import { IStaff } from '~/Models/DTO/Staff.model';
import { useStaffMenuActions } from './StaffItemMenu/useStaffMenuActions';
import { useStaffListColumns } from './useStaffListColumns';

export const useStaffList = () => {
  const { t } = useTranslation();
  const { currentProvider } = useProvider();
  const { navigate } = useNavigation();
  const { columnMap } = useStaffListColumns();
  const { actions, confirmationModalProps } = useStaffMenuActions();

  const defaultQuery = {
    disabled: 'false',
    providerId: currentProvider?.uuid || '',
  };

  const keyOfId: keyof IStaff = 'id';
  const settings: IFitoListSettings = {
    search: true,
    rearrange: false,
    pagination: true,
    headerPagination: {
      skip: 0,
      take: 50,
    },
  };

  const handleAddButtonClick = () => {
    navigate(routeReplacer(Route.PROVIDER_STAFF_CREATE, { domain: currentProvider?.domain || '' }));
  };

  const createListItem: (item: IStaff) => IFitoList = (item: IStaff) => {
    const ownerText = item.isOwner ? `(${t('manager.owner')})` : '';
    return {
      header: `${item.firstName} ${item.lastName} ${ownerText}`,
      subheader: [item.email, t(`settings.staff.accessType.${item.accessType}`)].join(' ･ '),
      onClick: () => navigate(routeReplacer(Route.PROVIDER_STAFF_EDIT, { id: item.id })),
    };
  };

  const setItemValues = (item: IStaff): IFitoList => createListItem(item);

  const tabs = [
    {
      name: t('app.active'),
      query: { disabled: 'false' },
    },
    {
      name: t('app.inactive'),
      query: { disabled: 'true' },
    },
  ];

  const tableDefinition: TableDefinition = useMemo(
    () => ({
      columns: [columnMap.image, columnMap.email, columnMap.role],
      config: { useSelectableRows: true, usePagination: false },
    }),
    [columnMap],
  );

  return {
    defaultQuery,
    keyOfId,
    settings,
    setItemValues,
    tabs,
    tableDefinition,
    handleAddButtonClick,
    actions,
    confirmationModalProps,
  };
};
